import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithHeaders } from './baseApi';

export interface ResultatSemestre {
  id: number;
  etudiant: number;
  etudiant_matricule: string;
  etudiant_nom: string;
  etudiant_niveau: string;
  etudiant_classe: string;
  etudiant_option: string;
  etudiant_pole?: string;
  etudiant_type_formation?: string; // 'FI' | 'DD' | 'CS'
  semestre: number;
  annee_academique?: string;
  moyenne_semestre: string | null;
  credits_obtenus: number;
  credits_requis: number;
  valide: boolean;
  doit_rattraper?: boolean;
  rang: number | null;
  effectif_classe: number | null;
  session_validation?: string;
  mention: string | null;
  decision: string;
  date_calcul: string;
}

export interface ResultatsFilters {
  etudiant__niveau?: string;
  etudiant__classe?: string;
  etudiant__option?: string;
  etudiant__pole?: string;
  annee_academique?: number;
  semestre?: number;
  valide?: boolean;
  search?: string;
}

export interface CalculNiveauResult {
  niveau: string;
  total_etudiants: number;
  etudiants_traites: number;
  total_ecue: number;
  total_ue: number;
  total_semestres: number;
  errors: string[];
}

export interface CalculEtudiantResult {
  etudiant: string;
  niveau: string;
  ecue_calculees: number;
  ue_calculees: number;
  semestres_calcules: number;
  errors: string[];
}

export const resultatsApi = createApi({
  reducerPath: 'resultatsApi',
  baseQuery: baseQueryWithHeaders,
  tagTypes: ['ResultatsSemestre'],
  endpoints: (builder) => ({
    getResultatsSemestre: builder.query<ResultatSemestre[], ResultatsFilters | void>({
      query: (params) => ({
        url: '/resultats-semestre/',
        params: params ?? undefined,
      }),
      transformResponse: (response: any) => {
        if (response && response.results) {
          return response.results;
        }
        return Array.isArray(response) ? response : [];
      },
      providesTags: ['ResultatsSemestre'],
    }),

    getEtudiantsSansResultat: builder.query<{
      semestre: 1 | 2;
      annee: string;
      total_periometre: number;
      sans_resultat: number;
      avec_resultat: number;
      resultats: Array<{
        id: number; matricule: string; nom: string; prenoms: string;
        classe: string; niveau: string; option: string; pole: string;
        nb_ues_attendues: number; nb_ues_calculees: number;
        ues_manquantes: Array<{ id: number; code: string; nom: string }>;
      }>;
    }, {
      semestre: 1 | 2;
      niveau?: string; pole?: string; option?: string; classe?: string;
      type_formation?: string;
    }>({
      query: (params) => ({
        url: '/resultats-semestre/etudiants-sans-resultat/',
        params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')),
      }),
    }),

    calculerToutNiveau: builder.mutation<CalculNiveauResult, {
      niveau: string;
      // Scope semestre optionnel (fix 2026-08-25) : 1 = impair (S1/S3/S5),
      // 2 = pair (S2/S4/S6), absent = les deux parités.
      semestre?: 1 | 2;
      classe_code?: string;
      type_formation?: string;
      // Filtres du Tableau délibération (fix 2026-09-17)
      pole?: string;
      option?: string;
    }>({
      query: (body) => ({
        url: '/resultats-semestre/calculer_tout_niveau/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ResultatsSemestre'],
    }),

    calculerToutEtudiant: builder.mutation<CalculEtudiantResult, { etudiant_id: number }>({
      query: (body) => ({
        url: '/resultats-semestre/calculer_tout_etudiant/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ResultatsSemestre'],
    }),

    recalculerCS: builder.mutation<{
      annee_academique: string;
      total_etudiants_cs: number;
      semestres_traites: number[];
      ecue_calculees: number;
      ue_calculees: number;
      semestres_calcules: number;
      erreurs: Array<{ matricule: string; message: string }>;
      classes_touchees: string[];
    }, {
      semestre?: 1 | 2;
      niveau?: string;
      classe_code?: string;
      option?: string;
      annee_academique?: string;
    }>({
      query: (body) => ({
        url: '/resultats-semestre/recalculer-cs/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['ResultatsSemestre'],
    }),

    getStatsDeliberation: builder.query<StatsDeliberationResponse, StatsDeliberationParams>({
      query: (params) => ({
        url: '/resultats-semestre/stats-deliberation/',
        params: params as any,
      }),
    }),

    getSyntheseDoubleDiplome: builder.query<SyntheseDDResponse, SyntheseDDParams>({
      query: (params) => ({
        url: '/resultats-semestre/synthese-double-diplome/',
        params: params as any,
      }),
    }),
  }),
});

export interface SyntheseDDParams {
  annee?: string;
  pole?: string;
  niveau?: string;
  option?: string;
}

export interface SyntheseDDStudent {
  matricule: string;
  nom: string;
  prenoms: string;
  niveau: string;
  option: string;
  pole: string;
  moyenne_fi: number | null;
  moyenne_dd: number | null;
  moyenne_generale: number | null;
  mention: string;
  nb_modules_dd: number;
}

export interface SyntheseDDAgg {
  libelle: string;
  effectif: number;
  moyenne_fi: number | null;
  moyenne_dd: number | null;
  moyenne_generale: number | null;
  nb_admis: number;
}

export interface SyntheseDDResponse {
  annee: string;
  effectif: number;
  students: SyntheseDDStudent[];
  par_option: SyntheseDDAgg[];
  par_niveau: SyntheseDDAgg[];
}

export interface StatsDeliberationParams {
  semestre: 1 | 2;
  annee?: string;
  /** Sans niveau ni classe : licence (défaut) ou master, jamais mélangés. */
  cycle?: 'licence' | 'master';
  pole?: string;
  option?: string;
  classe?: string;
  niveau?: string;
  type_formation?: 'FI_DD' | 'FI' | 'DD' | 'CS' | 'TOUS';
  seuil_rachat?: number;
}

export interface StatsSession {
  nb_participants: number;
  nb_absents: number;
  taux_participation: number;
  nb_admis: number;
  nb_ajournes?: number;
  nb_sans_resultat?: number;
  taux_admission: number;
  taux_ajournes?: number;
  nb_concernes?: number;
}

export interface StatsRachat {
  seuil: number;
  nb_total_ajournes: number;
  nb_eligibles_rachat: number;
  taux_rachat_sur_effectif: number;
  taux_rachat_sur_ajournes: number;
  nb_admis_apres_rachat: number;
  taux_admission_apres_rachat: number;
}

export interface BreakdownDeliberation {
  libelle: string;
  inscrits: number;
  admis_ord: number;
  admis_ratt: number;
  admis_total: number;
  ajournes: number;
  participants_ord: number;
  participants_ratt: number;
  taux_participation_ord: number;
  taux_reussite_ord: number;
  taux_reussite_total: number;
  taux_echec: number;
  moyenne_generale: number;
  sup14: number;
  sup16: number;
  /** inscrits − admis − ajournés : étudiants dont le semestre n'est pas calculé */
  sans_resultat?: number;
}

export interface StatsDeliberationResponse {
  annee: string;
  semestre: number;
  scope: {
    pole: string; option: string; classe: string;
    niveau: string; type_formation: string;
  };
  effectif_inscrits: number;
  nb_avec_resultat: number;
  // Identité : inscrits = admis (ordinaire + rattrapage) + ajournés finaux + sans résultat
  nb_sans_resultat?: number;
  nb_admis_total?: number;
  nb_ajournes_final?: number;
  taux_admission_total?: number;
  taux_ajournes_final?: number;
  taux_sans_resultat?: number;
  moyenne_generale?: number;
  nb_sup14?: number;
  nb_sup16?: number;
  session_ordinaire: StatsSession | null;
  session_rattrapage: StatsSession | null;
  rachat: StatsRachat | null;
  distribution_mention?: Array<{ mention: string; count: number }>;
  distribution_moyennes?: Array<{ tranche: string; count: number }>;
  par_niveau?: BreakdownDeliberation[];
  par_pole?: BreakdownDeliberation[];
  par_option?: BreakdownDeliberation[];
  par_niveau_pole?: BreakdownDeliberation[];
  par_niveau_option?: BreakdownDeliberation[];
  par_classe_top?: BreakdownDeliberation[];
}

export const {
  useGetResultatsSemestreQuery,
  useCalculerToutNiveauMutation,
  useCalculerToutEtudiantMutation,
  useRecalculerCSMutation,
  useGetStatsDeliberationQuery,
  useGetSyntheseDoubleDiplomeQuery,
  useGetEtudiantsSansResultatQuery,
  useLazyGetEtudiantsSansResultatQuery,
} = resultatsApi;
